import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const getMinValue = (key) => (key === "page" ? 0 : 1);

const getNumberParam = (params, key, fallback) => {
  const value = Number(params.get(key));
  return Number.isFinite(value) && value >= getMinValue(key) ? value : fallback;
};

const getStringParam = (params, key, fallback) => params.get(key) ?? fallback;

const isDefaultValue = (value, fallback) => String(value) === String(fallback);

const useTableUrlState = (defaults) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = Object.keys(defaults).reduce((result, key) => {
    const defaultValue = defaults[key];
    result[key] =
      typeof defaultValue === "number"
        ? getNumberParam(searchParams, key, defaultValue)
        : getStringParam(searchParams, key, defaultValue);
    return result;
  }, {});

  const updateTableState = useCallback(
    (updates) => {
      setSearchParams(
        (currentParams) => {
          const nextParams = new URLSearchParams(currentParams);

          Object.entries(updates).forEach(([key, value]) => {
            const defaultValue = defaults[key];
            const nextValue =
              typeof value === "function"
                ? value(
                    typeof defaultValue === "number"
                      ? getNumberParam(nextParams, key, defaultValue)
                      : getStringParam(nextParams, key, defaultValue),
                  )
                : value;
            const numericValue =
              typeof defaultValue === "number" ? Number(nextValue) : null;
            const isInvalidNumber =
              typeof defaultValue === "number" &&
              (!Number.isFinite(numericValue) ||
                numericValue < getMinValue(key));

            if (
              nextValue === undefined ||
              nextValue === null ||
              nextValue === "" ||
              isInvalidNumber ||
              isDefaultValue(nextValue, defaultValue)
            ) {
              nextParams.delete(key);
            } else {
              nextParams.set(key, String(nextValue));
            }
          });

          return nextParams;
        },
        { replace: true },
      );
    },
    [defaults, setSearchParams],
  );

  const getReturnState = useCallback(
    () => ({
      from: `${window.location.pathname}${window.location.search}`,
    }),
    [],
  );

  return [state, updateTableState, getReturnState];
};

export default useTableUrlState;
